import { useState } from "react";
import { Button, Form, Input, Modal, Select, Switch, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { SiteLabel } from "../../components/SiteLabel";
import { useApi } from "../../helpers/useApi";
import { useSitesQuery, useStockLocationsQuery } from "../../helpers/queries";
import { useQueryClient } from "@tanstack/react-query";
import type { StockLocation } from "../../api-models";

export default function StockLocationsPage() {
  const { data: locations = [] } = useStockLocationsQuery();
  const { data: sites = [] } = useSitesQuery();
  const { request } = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StockLocation | null>(null);
  const [form] = Form.useForm();

  function reload() {
    queryClient.invalidateQueries({ queryKey: ["stock-locations"] });
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    if (editing) await request({ method: "PUT", url: `/stock-locations/${editing.id}`, data: values });
    else await request({ method: "POST", url: "/stock-locations", data: values });
    setOpen(false);
    reload();
  }

  return (
    <div>
      <PageHeader
        title="Stock Locations"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              form.resetFields();
              setOpen(true);
            }}
          >
            New location
          </Button>
        }
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
        {locations.map((w) => (
          <div key={w.id} style={{ border: "1px solid #eee", borderRadius: 8, padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong>{w.name}</strong>
              <Tag color={w.active ? "green" : "default"}>{w.active ? "Active" : "Inactive"}</Tag>
            </div>
            <div>{w.siteId ? <SiteLabel site={w.site} /> : "Central"}</div>
            <Button
              type="link"
              onClick={() => {
                setEditing(w);
                form.setFieldsValue(w);
                setOpen(true);
              }}
            >
              Edit
            </Button>
          </div>
        ))}
      </div>
      <Modal title={editing ? "Edit location" : "New location"} open={open} onCancel={() => setOpen(false)} onOk={handleSubmit} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          {!editing && (
            <Form.Item name="siteId" label="Site (leave empty for central)">
              <Select allowClear options={sites.map((s) => ({ value: s.id, label: s.name }))} />
            </Form.Item>
          )}
          {editing && (
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
